import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Copy, History, Loader2, Sparkles, Trash2, X } from 'lucide-react'

import { aiApi, extractTextOutput } from '@/api/ai'
import { ConfirmDialog } from '@/shared/components/ConfirmDialog'
import { Button } from '@/shared/components/ui/button'
import { ScriptAIModelField } from '@/features/studio/components/ScriptAIModelField'
import { ProtoBtn, ProtoCard, ProtoTextarea } from '@/features/studio/components/ui-kit'
import { copyTextToClipboard } from '@/shared/lib/clipboard'
import { pollAITask } from '@/features/studio/hooks/pollAITask'
import { toast } from '@/shared/lib/notify'
import { useScriptAIHistory } from '@/features/studio/hooks/useScriptAIHistory'
import { useScriptTextModel } from '@/features/studio/hooks/useScriptTextModel'
import { buildStudioAIBillingPayload } from '@/features/studio/lib/studio-ai-billing'
import { resolveApiErrorMessage } from '@/features/studio/lib/resolveApiError'

function formatTime(ts: number, locale: string) {
  return new Date(ts).toLocaleString(locale, {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function summarizePrompt(prompt: string) {
  const s = prompt.trim().replace(/\s+/g, ' ')
  if (!s) return ''
  return s.length > 40 ? `${s.slice(0, 40)}…` : s
}

export function ScriptEditorAIPanel({ productionId }: { productionId: string }) {
  const { t, i18n } = useTranslation()
  const scriptModel = useScriptTextModel()
  const { modelId, selectedModel, ready: aiModelReady } = scriptModel
  const [prompt, setPrompt] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [clearHistoryOpen, setClearHistoryOpen] = useState(false)

  const history = useScriptAIHistory(`script-ai-history:${productionId}`)

  const activeText = history.active?.text ?? ''

  function costLabel(base: string) {
    if (!selectedModel) return base
    return `${base}（${t('script.aiModelCostUnit', { cost: selectedModel.cost })}）`
  }

  async function handleGenerate() {
    const trimmed = prompt.trim()
    if (!trimmed) return
    setBusy(true)
    setError(null)
    setCopied(false)
    try {
      if (!modelId) return
      const task = await aiApi.submitText({
        model_id: modelId,
        prompt: trimmed,
        payload: buildStudioAIBillingPayload(productionId),
      })
      const final = await pollAITask(task.id, () => {})
      if (final.status !== 'succeeded') {
        throw new Error(final.error_message || t('script.aiFailed'))
      }
      const text = extractTextOutput(final.output).trim()
      if (!text) {
        throw new Error(t('script.aiFailed'))
      }
      history.add({
        kind: 'generate',
        text,
        label: summarizePrompt(trimmed) || t('script.aiHistoryGenerate'),
        cost: final.cost ?? undefined,
        prompt: trimmed,
        modelName: selectedModel?.name,
      })
    } catch (e) {
      setError(t(resolveApiErrorMessage(e, 'script.aiFailed')))
    } finally {
      setBusy(false)
    }
  }

  async function handleCopy() {
    if (!activeText) return
    try {
      await copyTextToClipboard(activeText)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.error(t('script.aiCopyFailed'))
    }
  }

  const kindLabel = useMemo(
    () => ({
      generate: t('script.aiHistoryKindGenerate'),
      probe: t('script.aiHistoryKindGenerate'),
    }),
    [t],
  )

  return (
    <div className="flex h-full min-h-0 flex-col gap-2">
      <ProtoCard className="flex min-h-0 flex-1 flex-col !p-3">
        <div className="flex shrink-0 items-center justify-between gap-2">
          <p className="text-sm font-medium text-zinc-200">{t('script.aiResultTitle')}</p>
          <div className="flex items-center gap-1.5">
            <ProtoBtn
              size="sm"
              variant={history.historyOpen ? 'primary' : 'secondary'}
              onClick={() => history.setHistoryOpen((v) => !v)}
            >
              <History className="h-3.5 w-3.5" />
              {t('script.aiHistory')}
              {history.items.length > 0 ? (
                <span className="rounded-md bg-white/10 px-1.5 text-[10px]">{history.items.length}</span>
              ) : null}
            </ProtoBtn>
            <ProtoBtn size="sm" variant="secondary" disabled={!activeText} onClick={() => void handleCopy()}>
              <Copy className="h-3.5 w-3.5" />
              {copied ? t('script.aiCopied') : t('script.aiCopyResult')}
            </ProtoBtn>
            {activeText ? (
              <ProtoBtn size="sm" variant="ghost" className="text-zinc-500" onClick={() => history.clearDisplay()}>
                <X className="h-3.5 w-3.5" />
                {t('script.aiResultClear')}
              </ProtoBtn>
            ) : null}
          </div>
        </div>

        {history.historyOpen ? (
          <div className="mt-2 flex min-h-0 shrink-0 flex-col gap-2 border-b border-white/[0.06] pb-2">
            {history.items.length > 0 ? (
              <div className="flex justify-end">
                <ProtoBtn
                  size="sm"
                  variant="ghost"
                  className="text-zinc-500"
                  onClick={() => setClearHistoryOpen(true)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  {t('script.aiHistoryClear')}
                </ProtoBtn>
              </div>
            ) : null}
            <ul className="studio-scrollbar-hidden max-h-36 space-y-1 overflow-y-auto">
              {history.items.length === 0 ? (
                <li className="px-1 py-2 text-[11px] text-zinc-600">{t('script.aiHistoryEmpty')}</li>
              ) : (
                history.items.map((item) => {
                  const selected = item.id === history.activeId
                  return (
                    <li key={item.id}>
                      <div
                        className={`flex items-start gap-2 rounded-lg border px-2 py-1.5 transition ${
                          selected
                            ? 'border-indigo-500/35 bg-indigo-500/10'
                            : 'border-white/[0.06] bg-[#0e0e16] hover:border-white/[0.1]'
                        }`}
                      >
                        <Button
                          type="button"
                          variant="ghost"
                          className="h-auto min-w-0 flex-1 justify-start p-0 text-left"
                          onClick={() => history.setActiveId(item.id)}
                        >
                          <div className="flex items-center gap-1.5">
                            <span className="shrink-0 rounded bg-indigo-500/15 px-1 py-0.5 text-[9px] font-medium text-indigo-300">
                              {kindLabel[item.kind]}
                            </span>
                            <span className="truncate text-[11px] text-zinc-200">{item.label}</span>
                          </div>
                          <p className="mt-0.5 text-[10px] text-zinc-600">
                            {formatTime(item.createdAt, i18n.language)}
                            {item.cost != null ? ` · ${t('script.aiCost', { cost: item.cost })}` : ''}
                          </p>
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-xs"
                          title={t('common.delete')}
                          onClick={() => history.remove(item.id)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </li>
                  )
                })
              )}
            </ul>
          </div>
        ) : null}

        {history.active?.prompt ? (
          <p className="mt-2 shrink-0 text-[11px] text-zinc-400">{history.active.prompt}</p>
        ) : null}

        <ProtoTextarea
          className="studio-scrollbar-hidden mt-2 h-0 min-h-0 flex-1 !text-xs"
          readOnly
          value={activeText}
          placeholder={t('script.aiResultEmpty')}
        />
        {history.active ? (
          <p className="mt-1.5 shrink-0 text-[10px] text-zinc-600">
            {kindLabel[history.active.kind]} · {formatTime(history.active.createdAt, i18n.language)}
            {history.active.cost != null ? ` · ${t('script.aiCost', { cost: history.active.cost })}` : ''}
          </p>
        ) : null}
      </ProtoCard>

      <ProtoCard className="mt-auto flex max-h-[46%] min-h-[200px] shrink-0 flex-col overflow-visible !p-2.5">
        <div className="flex shrink-0 items-center gap-1.5 text-xs font-medium text-zinc-200">
          <Sparkles className="h-3.5 w-3.5 shrink-0 text-indigo-400" />
          {t('script.aiCreateTitle')}
        </div>

        <ProtoTextarea
          className="studio-scrollbar-hidden mt-2 h-0 min-h-[6.5rem] min-w-0 flex-1 !text-xs"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder={t('script.aiCreatePlaceholder')}
        />

        <div className="relative z-20 mt-2 shrink-0 overflow-visible">
          <ScriptAIModelField
            {...scriptModel}
            disabled={busy}
            compact
            inline
            trailing={
              <ProtoBtn
                size="sm"
                className="h-9 shrink-0"
                disabled={busy || !aiModelReady || !prompt.trim()}
                onClick={() => void handleGenerate()}
              >
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                {costLabel(t('script.aiCreateBtn'))}
              </ProtoBtn>
            }
          />
        </div>

        {error ? (
          <p className="mt-1.5 flex shrink-0 items-start gap-1.5 text-[10px] text-red-300">
            <span className="min-w-0 flex-1">{error}</span>
            <Button type="button" variant="link" size="icon-xs" className="shrink-0 text-red-400/80 hover:text-red-200" onClick={() => setError(null)}>
              <X className="h-3 w-3" />
            </Button>
          </p>
        ) : null}
      </ProtoCard>

      <ConfirmDialog
        open={clearHistoryOpen}
        title={t('script.aiHistoryClearTitle')}
        message={t('script.aiHistoryClearConfirm')}
        icon={Trash2}
        destructive
        confirmLabel={t('script.aiHistoryClear')}
        cancelLabel={t('common.cancel')}
        onConfirm={() => {
          history.clearAll()
          setClearHistoryOpen(false)
        }}
        onCancel={() => setClearHistoryOpen(false)}
      />
    </div>
  )
}
